// Space, track and resource bricks (presentation-2 contract sections 5.3 and 5.4): space-board,
// track, resource-tray and piece-pool.

import type { BrickInstance } from '../presentation/types';
import type { CounterView } from '../model/table';
import { useScene, zonesFor, countersFor, seatOf } from '../scene/SceneContext';
import { Piece } from './Piece';
import { targetProps, usePlay } from '../play/PlayController';
import { usePresentation } from '../presentation/PresentationContext';
import { Slot } from '../components/ObjectCard';
import { Glyph } from '../presentation/Glyph';
import { ZoneFrame } from './cards';
import { Pips, fieldOf, useOccupants } from './occupants';
import { zonesOf } from '../scene/SceneContext';
import { counterObject } from './counterObjects';

interface BrickProps { inst: BrickInstance; seat: string | null }

const PIECE_ICONS: Record<string, string> = { meeple: 'gi:meeple', train: 'gi:steam-locomotive', disc: 'circle', cube: 'gi:cube', pawn: 'gi:chess-pawn' };

const SPACE_WIDTHS = { s: 120, m: 170, l: 220 } as const;

export function SpaceBoard({ inst, seat }: BrickProps) {
  const { table, presentation } = useScene();
  const own = seatOf(inst, seat, table.selfSeat);
  const zone = zonesFor(inst, table, own)[0];
  const occupantsOf = useOccupants(inst, own);
  if (!zone) return null;
  const columns = (inst.props?.columns as number | undefined) ?? 4;
  const capacityField = (inst.props?.capacityField as string | undefined) ?? 'capacity';
  const countField = inst.props?.countField as string | undefined;
  const w = (inst.props?.width as number | undefined) ?? SPACE_WIDTHS[(inst.props?.size as keyof typeof SPACE_WIDTHS | undefined) ?? 'm'];
  const compact = !!inst.props?.compact;
  // `pips: false` draws no occupancy dots (a list of territories or guide cards has no seats to fill).
  const showPips = inst.props?.pips !== false;
  // Optional groups: `groupBy` a field path, `groupMap` a theme map from value to group label, in `groupOrder`.
  const groupBy = inst.props?.groupBy as string | undefined;
  const map = inst.props?.groupMap ? presentation.theme.maps?.[inst.props.groupMap as string] : undefined;
  const order = (inst.props?.groupOrder as string[] | undefined) ?? [];
  const groups: { label: string; objects: typeof zone.objects }[] = [];
  for (const o of zone.objects) {
    const raw = groupBy ? String(fieldOf(o, groupBy) ?? '') : '';
    const label = map?.[raw] ?? raw;
    const g = groups.find(x => x.label === label) ?? (groups.push({ label, objects: [] }), groups[groups.length - 1]!);
    g.objects.push(o);
  }
  const rank = (label: string) => { const i = order.indexOf(label); return i < 0 ? order.length : i; };
  groups.sort((a, b) => rank(a.label) - rank(b.label));
  const tile = (o: (typeof zone.objects)[number]) => {
    const cap = fieldOf(o, capacityField);
    const capacity = typeof cap === 'number' ? cap : null;
    const occupants = occupantsOf(o.id);
    const count = Math.max(occupants.length, countField ? Number(fieldOf(o, countField) ?? 0) || 0 : 0);
    const full = capacity !== null && count >= capacity;
    const blocked = o.fields.blocked === true;
    return (
      <Piece key={o.id} object={o} zoneKind={zone.kind} width={w} className={`space-tile${compact ? ' is-compact' : ''}${full ? ' is-full' : ''}${blocked ? ' is-blocked' : ''}`}
        hint={compact ? [o.title, o.catalog?.text].filter(Boolean).join(': ') : undefined}
        badge={showPips ? <Pips occupants={occupants} count={count} capacity={capacity} /> : undefined} />
    );
  };
  if (groupBy) return (
    <ZoneFrame zone={zone} label={inst.props?.label as string | undefined} className={`space-board is-grouped${compact ? ' is-compact' : ''}`}>
      {groups.map(g => (
        <div key={g.label} className="space-group" role="group" aria-label={g.label}>
          <h4 className="space-group-title">{g.label}</h4>
          <div className="space-items" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, ${w}px))` }}>{g.objects.map(tile)}</div>
        </div>
      ))}
    </ZoneFrame>
  );
  return (
    <ZoneFrame zone={zone} label={inst.props?.label as string | undefined} className={`space-board${compact ? ' is-compact' : ''}`}>
      <div className="space-items" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {zone.objects.map(o => {
          const cap = fieldOf(o, capacityField);
          const capacity = typeof cap === 'number' ? cap : null;
          const occupants = occupantsOf(o.id);
          const count = Math.max(occupants.length, countField ? Number(fieldOf(o, countField) ?? 0) || 0 : 0);
          const full = capacity !== null && count >= capacity;
          const blocked = o.fields.blocked === true;
          return (
            <Piece key={o.id} object={o} zoneKind={zone.kind} width={w} className={`space-tile${full ? ' is-full' : ''}${blocked ? ' is-blocked' : ''}`}
              badge={showPips ? <Pips occupants={occupants} count={count} capacity={capacity} /> : undefined} />
          );
        })}
      </div>
    </ZoneFrame>
  );
}

export function Track({ inst, seat }: BrickProps) {
  const { table, seatColor, seatLabel } = useScene();
  const own = seatOf(inst, seat, table.selfSeat);
  const counters = countersFor(inst, table, own).filter(c => typeof c.value === 'number');
  const zone = counters.length ? null : zonesFor(inst, table, own)[0] ?? null;
  const values = counters.map(c => Number(c.value));
  const length = (inst.props?.length as number | undefined) ?? Math.max(10, Math.ceil((Math.max(0, ...values) + 1) / 25) * 25);
  const label = (inst.props?.label as string | undefined) ?? counters[0]?.label ?? zone?.label ?? 'Track';
  const ticks = Array.from({ length: Math.floor(length / 5) + 1 }, (_, i) => i * 5);
  return (
    <section className={`track track-shape-${(inst.props?.shape as string | undefined) ?? 'line'}`} aria-label={label}>
      <h3 className="brick-title">{label}</h3>
      <div className="track-line">
        {ticks.map(t => <span key={t} className={`track-tick${t % 25 === 0 ? ' is-major' : ''}`} style={{ left: `${(t / length) * 100}%` }}>{t % 25 === 0 ? t : ''}</span>)}
        {counters.map((c, i) => {
          const v = Math.max(0, Math.min(length, Number(c.value)));
          const same = counters.slice(0, i).filter(x => Number(x.value) === Number(c.value)).length;
          return (
            <span key={c.id} className="track-marker" data-motion-counter={c.id} role="img" aria-label={`${seatLabel(c.owner)}: ${c.value}`}
              style={{ left: `${(v / length) * 100}%`, '--seat-color': seatColor(c.owner), '--stack': same } as React.CSSProperties}>
              <span className="track-value">{String(c.value)}</span>
            </span>
          );
        })}
      </div>
    </section>
  );
}

export function ResourceTray({ inst, seat }: BrickProps) {
  const { table } = useScene();
  const api = usePresentation();
  const play = usePlay();
  const own = seatOf(inst, seat, table.selfSeat);
  let counters = countersFor(inst, table, own);
  const order = inst.props?.order as string[] | undefined;
  if (order) counters = [...counters].sort((a, b) => rank(order, a) - rank(order, b));
  const style = (inst.props?.style as string | undefined) ?? 'chips';
  if (counters.length === 0) return null;
  const [lead, ...rest] = counters;
  const map = inst.props?.colorMap ? api.presentation?.theme.maps?.[inst.props.colorMap as string] : undefined;
  const colorOf = (c: CounterView) => (map && c.key ? map[c.key] : undefined);
  const objectZones = inst.props?.objectsFrom ? zonesOf(String(inst.props.objectsFrom), table, own) : [];
  const objects = objectZones.flatMap(z => z.objects);
  const objectField = String(inst.props?.objectField ?? 'resource');
  const draw = (c: CounterView, big = false) => <Counter key={c.id} c={c} big={big} api={api} play={play} color={colorOf(c)} object={counterObject(c, objects, objectField)} />;
  return (
    <section className={`resource-tray tray-${style}${inst.props?.columns ? ' has-columns' : ''}${inst.props?.compact ? ' is-compact' : ''}${inst.props?.large ? ' is-large' : ''}`} aria-label={(inst.props?.label as string | undefined) ?? 'Resources'}
      style={inst.props?.columns ? ({ '--tray-columns': inst.props.columns } as React.CSSProperties) : undefined}>
      {inst.props?.label ? <h3 className="brick-title">{inst.props.label as string}</h3> : null}
      {style === 'bars' && lead ? (
        <>
          {draw(lead, true)}
          <div className="tray-rest">{rest.map(c => draw(c))}</div>
        </>
      ) : inst.props?.columns
        ? <div className="tray-grid">{counters.map(c => draw(c))}</div>
        : counters.map(c => draw(c))}
    </section>
  );
}

function rank(order: string[], c: CounterView): number {
  const i = order.findIndex(k => k === c.key || k === c.id);
  return i < 0 ? order.length : i;
}

function Counter({ c, big = false, api, play, color, object }: { c: CounterView; big?: boolean; api: ReturnType<typeof usePresentation>; play: ReturnType<typeof usePlay>; color?: string; object?: import('../model/table').ObjectView | null }) {
  const icon = c.key ? api.icon(c.key) : null;
  const anchor = object ? `obj:${object.id}` : `counter:${c.id}`;
  const st = play?.stateOf(anchor);
  const counterState = object ? play?.stateOf(`counter:${c.id}`) : null;
  const counterActive = !!(counterState?.selectable || counterState?.option);
  const delegate = counterActive && !st?.selectable && !st?.option && !st?.selected;
  const t = targetProps(delegate ? null : play, anchor, `${c.label}: ${c.value}`, `tray-counter${big ? ' is-big' : ''}`);
  const numeric = typeof c.value === 'number' ? c.value : null;
  const inner = (
    <div {...t} {...(delegate ? { onClick: undefined, onKeyDown: undefined } : {})} role={!delegate && (st?.selectable || st?.option) ? 'button' : 'group'} tabIndex={!delegate && (st?.selectable || st?.option) ? 0 : -1} data-motion-counter={c.id} {...(object ? { 'data-motion-object': object.id } : {})} style={color ? ({ '--res-color': color } as React.CSSProperties) : undefined}>
      {icon && <span className="tray-icon" aria-hidden="true"><Slot value={icon} /></span>}
      <span className="tray-value">{String(c.value)}{c.max !== null ? <span className="tray-max">/{c.max}</span> : null}</span>
      <span className="tray-label">{c.label}</span>
      {c.max !== null && numeric !== null && <span className="tray-meter" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, (numeric / c.max) * 100))}%` }} /></span>}
    </div>
  );
  if (!object) return inner;
  const counterTarget = targetProps(play, `counter:${c.id}`, `${c.label}: ${c.value}`, 'tray-object-wrapper');
  return (
    <div {...counterTarget} role={counterActive ? 'button' : 'group'} tabIndex={counterActive ? 0 : -1}>{inner}</div>
  );
}

export function PiecePool({ inst, seat }: BrickProps) {
  const { table, seatColor } = useScene();
  const play = usePlay();
  const own = seatOf(inst, seat, table.selfSeat);
  const zone = zonesFor(inst, table, own)[0] ?? null;
  const counter = zone ? null : countersFor(inst, table, own)[0] ?? null;
  if (!zone && !counter) return null;
  const count = zone ? zone.count ?? zone.objects.length : Number(counter!.value);
  const max = (inst.props?.max as number | undefined) ?? counter?.max ?? null;
  const piece = (inst.props?.piece as string | undefined) ?? 'disc';
  const label = (inst.props?.label as string | undefined) ?? zone?.label ?? counter!.label;
  const anchor = zone ? `pool:${zone.id}` : `pool:counter:${counter!.id}`;
  const owner = zone?.owner ?? counter?.owner ?? own;
  if (inst.props?.style === 'pips') {
    // Ready pieces in the seat color, then the pieces in use (from `usedFrom`) as dashed outlines.
    const usedFrom = inst.props?.usedFrom as string | undefined;
    const used = usedFrom ? zonesOf(usedFrom, table, owner).reduce((n, z) => n + z.objects.filter(o => !o.owner || o.owner === owner).length, 0) : 0;
    const t = targetProps(play, anchor, `${label}: ${count} ready, ${used} in use`, 'piece-pool pool-pips');
    return (
      <div {...t} {...(zone ? { 'data-motion-zone': zone.id } : { 'data-motion-counter': counter!.id })} style={{ '--seat-color': seatColor(owner) } as React.CSSProperties}>
        <span className="pool-label">{label}</span>
        <span className="pool-pip-row" aria-hidden="true">
          {Array.from({ length: Math.min(count, 12) }, (_, i) => <span key={`r${i}`} className="pool-pip is-ready"><Glyph name={PIECE_ICONS[piece] ?? 'circle'} size={30} /></span>)}
          {Array.from({ length: Math.min(used, 12) }, (_, i) => <span key={`u${i}`} className="pool-pip is-used"><Glyph name={PIECE_ICONS[piece] ?? 'circle'} size={30} /></span>)}
        </span>
        <span className="pool-caption">{count} ready{used ? ` · ${used} out` : ''}</span>
      </div>
    );
  }
  const t = targetProps(play, anchor, `${label}: ${count}`, 'piece-pool');
  return (
    <div {...t} {...(zone ? { 'data-motion-zone': zone.id } : { 'data-motion-counter': counter!.id })} style={{ '--seat-color': seatColor(owner) } as React.CSSProperties}>
      <span className="pool-icon" aria-hidden="true"><Glyph name={PIECE_ICONS[piece] ?? 'circle'} size={28} /></span>
      <span className="pool-count">{count}{max !== null && <span className="pool-max">/{max}</span>}</span>
      <span className="pool-label">{label}</span>
      {max !== null && <span className="pool-meter" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, (count / max) * 100))}%` }} /></span>}
    </div>
  );
}
